/**
 * Time to impact: per settlement, the warning left on the exercise clock.
 *
 * Remaining = the settlement's arrival time (the first output where the run's
 * depth there rises past the arrival threshold, from /api/alerts) minus the
 * page clock, which the dispatch timeline drives. Coloured by the settlement's
 * CAP severity. The largest figure on the page.
 */

import { SEVERITY_COLOUR, clock, hm } from './shared'
import type { SettlementRow } from './shared'
import { fs } from '../../design/tokens'

interface Props {
  rows: SettlementRow[]
  t: number
  playing: boolean
  onPlayToggle: () => void
}

export function TimeToImpact({ rows, t, playing, onPlayToggle }: Props) {
  const sorted = [...rows].sort((a, b) => (a.arrival_time_min as number) - (b.arrival_time_min as number))
  if (!sorted.length) return null

  return (
    <section className="panel mb-4" data-testid="time-to-impact">
      <div className="panel-header">
        <span className="panel-title">Time to impact</span>
        <div className="flex items-center gap-3">
          <span className="section-label">Exercise clock</span>
          <span className="font-mono text-14 tabular-nums text-slate-100" data-testid="impact-clock">
            {clock(t)}
          </span>
          <button
            type="button"
            className="ctl rounded border border-slate-700 px-3 text-14 text-slate-100 hover:border-slate-500"
            onClick={onPlayToggle}
          >
            {playing ? 'Pause' : 'Play'}
          </button>
        </div>
      </div>
      <div
        className="grid gap-px"
        style={{
          gridTemplateColumns: `repeat(${Math.min(sorted.length, 4)}, minmax(0, 1fr))`,
          background: 'var(--ink-border)',
        }}
      >
        {sorted.map((s) => {
          const arrival = s.arrival_time_min as number
          const remaining = Math.max(0, arrival - t)
          const whole = Math.floor(remaining)
          const colour = SEVERITY_COLOUR[s.alert.severity ?? ''] ?? 'var(--ink-text)'
          return (
            <div
              key={s.settlement_id}
              className="px-4 py-3"
              style={{ background: 'var(--ink-surface)', borderTop: `3px solid ${colour}` }}
              data-testid="impact-countdown"
              data-settlement={s.name}
              data-remaining-min={whole}
            >
              <div className="flex items-baseline justify-between gap-3">
                <span className="truncate text-14 font-medium text-slate-100">{s.name}</span>
                <span className="text-14 font-semibold" style={{ color: colour }}>
                  {(s.alert.severity ?? '').toUpperCase()}
                </span>
              </div>
              <div className="mt-1 flex items-baseline gap-2 font-mono tabular-nums" style={{ color: colour }}>
                <span style={{ fontSize: fs(48), lineHeight: 1, fontWeight: 600 }}>{Math.floor(whole / 60)}</span>
                <span className="text-14">h</span>
                <span style={{ fontSize: fs(48), lineHeight: 1, fontWeight: 600 }}>
                  {String(whole % 60).padStart(2, '0')}
                </span>
                <span className="text-14">min</span>
              </div>
              <div className="mt-1 font-mono text-14 text-slate-400">
                arrival {clock(arrival)}
                {s.alert.lead_time_min !== null && ` · lead time ${hm(s.alert.lead_time_min)}`}
              </div>
            </div>
          )
        })}
      </div>
    </section>
  )
}
